import React, { useEffect } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import styled from 'styled-components';
import BeneficiaryAge from './BeneficiaryAge';
import BeneficiaryBottomSection from './BeneficiaryBottomSection';
import BeneficiaryEthnic from './BeneficiaryCategories/BeneficiaryEthnic';
import BeneficiaryGender from './BeneficiaryCategories/BeneficiaryGender';
import BeneficiaryHealth from './BeneficiaryCategories/BeneficiaryHealth';
import BeneficiarySocial from './BeneficiaryCategories/BeneficiarySocial';
import BeneficiaryWork from './BeneficiaryCategories/BeneficiaryWork';
import { MainCategoryProps } from './MainCategory';
import { BeneficiaryTitleInfoProps } from './utils/beneficiaryInterfaces';
import { INDICATOR_DETAILS_QUESTION_ID } from '../../../NPOFeedback/utils';
import { getReachIndicator } from '../IndicatorDetails/IndicatorDetailQuestions/utils';
import { parseImpactDetailsAnswer } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails';
import { getSentenceStructureUnit } from '../ImpactIndicator/IndicatorCardDetails';
import { numberWithCommas } from 'shared/utils/numberUtils';

const StyledDiv = styled.div`
  max-width: 979px;
  display: flex;
  margin-bottom: 40px;
  .heading-container {
    padding-right: 25px;
    max-width: 720px;
    margin: 0 auto;
  }
  .heading-page {
    margin-bottom: 22px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const defaultTitle =
  'What percentage of the populations of your selected categories does your program serve?';
const TopContainer: React.FC<{
  title: string | JSX.Element;
}> = React.memo(({ title }) => {
  return (
    <StyledDiv>
      <div className="heading-container">
        <TrueImpactText
          variant="heading-page"
          className="heading-page"
          align="center"
        >
          {title}
        </TrueImpactText>
        <TrueImpactText className="body-text" align="center">
          Estimates are perfectly fine for this. We're trying to understand the
          relevant demographics of people being served by your program and the
          associated percentage for each demographic category.
        </TrueImpactText>
      </div>
    </StyledDiv>
  );
});

const BeneficiaryCategories: React.FC<MainCategoryProps &
  BeneficiaryTitleInfoProps> = ({
  allAnswers,
  beneficiaries,
  beneficiary,
  setBeneficiaries,
  programName,
  goBack,
}) => {
  const intermediaries = allAnswers[40022];
  const beneficiaryName = allAnswers[40004];
  const serviceType = allAnswers[40006];
  const reachIndicator = getReachIndicator(
    parseImpactDetailsAnswer(allAnswers[INDICATOR_DETAILS_QUESTION_ID])?.reach,
  );
  let title = defaultTitle;
  let beneficiaryLabel = beneficiary;
  if (reachIndicator?.forecast && beneficiaryName) {
    beneficiaryLabel = getSentenceStructureUnit(
      undefined,
      undefined,
      beneficiaryName,
      intermediaries,
      serviceType,
    );
    title = `What percentage of the ${numberWithCommas(
      reachIndicator.forecast,
    )} ${beneficiaryLabel} served fall into your selected categories?`;
  }

  useEffect(() => {
    // scroll to the top
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  return (
    <PageContainer>
      <TopContainer title={title} />
      <ContentContainer>
        <BeneficiaryAge
          allAnswers={allAnswers}
          beneficiary={beneficiaries.age}
          beneficiaryLabel={beneficiaryLabel}
          setBeneficiaries={setBeneficiaries}
          programName={programName}
        />
        <BeneficiaryEthnic
          allAnswers={allAnswers}
          beneficiary={beneficiaries.ethnic}
          beneficiaryLabel={beneficiaryLabel}
          setBeneficiaries={setBeneficiaries}
          programName={programName}
        />
        <BeneficiaryGender
          allAnswers={allAnswers}
          beneficiary={beneficiaries.gender}
          beneficiaryLabel={beneficiaryLabel}
          setBeneficiaries={setBeneficiaries}
          programName={programName}
        />
        <BeneficiaryHealth
          allAnswers={allAnswers}
          beneficiary={beneficiaries.health}
          beneficiaryLabel={beneficiaryLabel}
          setBeneficiaries={setBeneficiaries}
          programName={programName}
        />
        <BeneficiarySocial
          allAnswers={allAnswers}
          beneficiary={beneficiaries.social}
          setBeneficiaries={setBeneficiaries}
          beneficiaryLabel={beneficiaryLabel}
          programName={programName}
        />
        <BeneficiaryWork
          allAnswers={allAnswers}
          beneficiary={beneficiaries.work}
          beneficiaryLabel={beneficiaryLabel}
          setBeneficiaries={setBeneficiaries}
          programName={programName}
        />
      </ContentContainer>
      <BeneficiaryBottomSection goBack={goBack} />
    </PageContainer>
  );
};

export default BeneficiaryCategories;
